<template>
  <div
    class="product-card-wrapper"
    :data-test-id="wrapperDataTestId"
    :class="{
      'product-card-wrapper--slide': isSlide,
      'product-card-wrapper--promo': isPromoCard,
      'product-card-wrapper--on-wishlist': isOnWishlist,
      'product-card-wrapper--mobile': isMobileView,
    }"
  >
    <component
      :is="link ? 'a' : 'div'"
      class="product-card"
      :href="localePath(getFullLink)"
      :target="openInNewTab ? '_blank' : '_self'"
      @click="handleClick($event)"
    >
      <ProductCardImage
        v-bind="props"
        :badges="badges"
        :image="Array.isArray(props.image) ? props.image[0] : props.image"
      >
        <ProductCardWishlist
          v-if="!isPromoCard && !disableWishlist"
          :is-on-wishlist="isOnWishlist"
          @click.stop.prevent="handleClickWishlist"
        />
      </ProductCardImage>

      <div class="product-card__content">
        <slot name="before-title"></slot>
        <div
          v-if="title"
          class="product-card__title"
          data-test-id="kft-product-card-title"
        >
          {{ title }}
        </div>
        <span v-if="isFeatureEnabled('next-date-on-product-card')">
          {{ nextEventDateTime }}
        </span>

        <!-- Custom slot for content below title -->
        <slot name="content-below-title">
          <ProductCardInformation v-bind="props" />
          <div v-if="regularPrice" class="product-card__price">
            <div
              v-if="finalPrice"
              :class="{
                'product-card__price__discounted-price': originalPrice,
              }"
            >
              {{ finalPriceText }}
            </div>
            <div
              v-if="originalPrice"
              class="product-card__price__original-price"
            >
              {{ formatPriceAmount(Number(originalPrice), locale, 'EUR', 0) }}
            </div>
            <span
              v-if="hasShippingCosts"
              class="text-gray-500 text-xs self-center"
              >{{ t('product.show.checkout.shipping.productCardInfo') }}
            </span>
          </div>
        </slot>
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { formatPriceAmount } from '@/utils/currencyHelpers'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'

const { isFeatureEnabled } = useFeatureToggle()
const $localePath = useLocalePath()
const localePath = $localePath
const { locale, t } = useI18n()

interface Props {
  openInNewTab?: boolean
  image?: string | string[] | Record<string, any>
  badges?: ProductCardImageBadge[]
  productType?: string
  duration?: string
  durationTitle?: string
  isDurationVisible?: boolean
  rating?: number | string | null
  nextEventDateTime?: string
  productCategory?: string
  title?: string
  alt?: string
  link?: string
  scoreRating?: number | boolean
  reviewsCount?: number | boolean
  maxRating?: number | string
  regularPrice?: number | string | null
  specialPrice?: number | string | null
  isOnWishlist?: boolean
  isPromoCard?: boolean
  isMobileView?: boolean
  isSpecialGiftcard?: boolean
  hasShippingCosts?: boolean
  minNumberOfPeople?: number
  maxNumberOfPeople?: number
  tags?: string[]
  isSlide?: boolean
  isTeamevent?: boolean
  qid?: string | null
  disableWishlist?: boolean
  beforeNavigationCallback?: () => void
  isBoxEvent?: boolean
  isPartnerRating?: boolean
  isThirdPartyRating?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  openInNewTab: false,
  image: '',
  badges: () => [],
  productType: '',
  duration: '',
  durationTitle: '',
  isDurationVisible: true,
  rating: null,
  nextEventDateTime: '',
  productCategory: '',
  title: '',
  alt: '',
  link: '',
  scoreRating: false,
  reviewsCount: false,
  maxRating: 5,
  regularPrice: null,
  specialPrice: null,
  isOnWishlist: false,
  isPromoCard: false,
  isSpecialGiftcard: false,
  hasShippingCosts: false,
  isTeamevent: false,
  tags: () => [],
  isSlide: false,
  minNumberOfPeople: 0,
  maxNumberOfPeople: 0,
  qid: null,
  disableWishlist: false,
  beforeNavigationCallback: undefined,
  isBoxEvent: false,
  isPartnerRating: false,
  isThirdPartyRating: false,
})

const finalPrice = computed(() =>
  Number(props.specialPrice || props.regularPrice)
)

const originalPrice = computed(() =>
  props.specialPrice ? props.regularPrice : undefined
)

const finalPriceText = computed(() => {
  const priceString = formatPriceAmount(finalPrice.value, locale.value, 'EUR', 0)

  if (props.isSpecialGiftcard || props.isBoxEvent) {
    return priceString
  }

  return t('product.card.price.priceText', {
    price: priceString,
  })
})

const emit = defineEmits(['click:wishlist'])

const wrapperDataTestId = computed(() => {
  if (props.isSpecialGiftcard) {
    return 'special-giftcard-product-card'
  }

  return props.isPromoCard ? 'promotion-card' : 'product-card'
})

const handleClickWishlist = () => {
  emit('click:wishlist')
}

const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const getFullLink: ComputedRef<string> = computed(() => {
  if (isMounted.value && props.qid) {
    const hasQuery = props.link?.includes('?')
    const separator = hasQuery ? '&' : '?'
    return `${props.link}${separator}qid=${props.qid}`
  }

  return props.link
})

const handleClick = (event: Event) => {
  // Only intercept if there's a callback, otherwise let default link behavior work
  if (props.beforeNavigationCallback) {
    event.preventDefault()

    props.beforeNavigationCallback()

    const fullLink = localePath(getFullLink.value)

    // Open window FIRST while we still have the user gesture context
    // This is critical: window.open() must be called synchronously in direct
    // response to the user click, otherwise popup blockers may block it
    let openedWindow: Window | null = null
    if (props.openInNewTab) {
      openedWindow = window.open(fullLink, '_blank')
      // If popup was blocked, openedWindow will be null
      if (!openedWindow) {
        // Popup blocker detected - fall back to same tab navigation
        console.warn('Popup blocked, falling back to same tab navigation')
        window.location.href = fullLink
        props.beforeNavigationCallback()
        return
      }
    }

    // Execute callback (may be async, but navigation is already initiated)
    props.beforeNavigationCallback()

    // If not opening in new tab, navigate after callback
    if (!props.openInNewTab) {
      window.location.href = fullLink
    }
  }
}
</script>

<style lang="postcss" scoped>
.product-card-wrapper {
  @apply flex relative;

  &:not(&--slide) {
    @apply w-full;
  }

  &--slide {
    @apply h-auto min-w-[170px];
    width: 100%;
  }

  .swiper-slide & {
    height: 100%;
  }
}

.product-card {
  @apply w-full flex relative bg-white p-1 ease-in-out duration-300 rounded-xl overflow-hidden;

  .product-card-wrapper:not(.product-card-wrapper--mobile) & {
    @apply md:flex-col;
  }

  .product-card-wrapper--promo & {
    @apply text-white;
    background: rgba(252, 255, 238, 0.65);
  }

  .swiper-slide & {
    @apply flex-col;
  }

  &:hover {
    @apply md:translate-y-[-3px];
  }

  &__content {
    width: calc(100% - 172px);
    @media (min-width: 0px) and (max-width: 365px) {
      width: calc(100% - 150px);
    }

    @apply pl-2 flex flex-col grow;
    .product-card-wrapper:not(.product-card-wrapper--mobile) & {
      @apply md:px-1 md:w-full md:pt-1.5;
    }

    /* styles for swiper slide */

    .swiper-slide & {
      @apply w-full p-0 pt-1.5;
    }
  }

  &__title {
    @apply font-medium duration-300 text-stone-700 text-sm md:text-base mb-1;
    line-height: 1.4;

    .product-card-wrapper--mobile & {
      @apply truncate;
    }
  }

  &__price {
    @apply font-medium text-sm md:text-base mb-0 mt-1 text-stone-700 flex gap-1 items-end;
    letter-spacing: 0.01em;

    &__original-price {
      @apply line-through text-xs leading-5 text-stone-500;
    }

    &__discounted-price {
      @apply font-semibold text-red-600;
    }
  }
}
</style>
